import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus, MessageCircle } from 'lucide-react';
import Section from '../components/Section';
import { Link } from 'react-router-dom';

// SSS Verisi
const faqData = [
  {
    category: "Genel",
    questions: [
      {
        id: 1,
        question: "Lotus Sport nedir ve neyi amaçlar?",
        answer: "Lotus Sport, yapay zeka destekli sağlık ve fitness teknolojileri geliştiren bir girişimdir. Amacımız, bireylerin sağlık ve spor aktivitelerini kişiselleştirilmiş, erişilebilir ve bilimsel verilerle desteklenmiş bir şekilde gerçekleştirmelerini sağlamaktır."
      },
      {
        id: 2,
        question: "Exereyes uygulaması ücretli mi?",
        answer: "Exereyes uygulamamız, temel özellikleriyle tamamen ücretsiz olarak sunulmaktadır. Toplumsal kapsayıcılık misyonumuz gereği, herkesin kaliteli egzersiz rehberliğine erişebilmesini önemsiyoruz."
      },
      {
        id: 3,
        question: "Uygulamayı kullanmak için spor ekipmanına ihtiyacım var mı?",
        answer: "Hayır! Exereyes'ın en büyük yeniliği, evinizdeki gündelik nesneleri (sandalye, su şişesi, havlu vb.) spor ekipmanına dönüştürebilmesidir. Yapay zeka teknolojimiz ortamı tarar ve size en uygun programı sunar."
      }
    ]
  },
  {
    category: "Teknik & Güvenlik",
    questions: [
      {
        id: 4,
        question: "Verilerim güvende mi?",
        answer: "Kesinlikle. Kullanıcı verilerinin gizliliği bizim için en öncelikli konudur. Tüm verileriniz KVKK ve GDPR standartlarına uygun olarak şifrelenir ve saklanır. Kamera görüntüleri anlık analiz için kullanılır ve asla sunucularımıza kaydedilmez."
      },
      {
        id: 5,
        question: "Hangi cihazlarda kullanabilirim?",
        answer: "Lotus Sport uygulamaları (Exereyes dahil), Android işletim sistemine sahip güncel akıllı telefonlarda ve tabletlerde sorunsuz çalışacak şekilde optimize edilmiştir. IOS için en kısa sürede destek olunacaktır."
      },
    ]
  },
  {
      category: "Exereyes Mobil Uygulaması",
      questions: [
        {
          id: 6,
          question: "iOS/Android için nasıl indirebilirim?",
          answer: "Android'de Google Play üzerinden 'Exereyes' araması yapıp indirebilirsiniz. Uygulama şu anda Google Play Store'da yayında ve kullanıma hazır. iOS için App Store'da yakında bulunacaktır. Güncel kalmak için otomatik güncellemeleri açık bırakın."
        },
        {
          id: 7,
          question: "İlk giriş ve aktivasyon nasıl yapılır?",
          answer: "Kurulumdan sonra telefon numaranızla giriş yapın, SMS doğrulamasını tamamlayın ve profil bilgilerinizi kaydedin. İlk önerileriniz bu bilgilere göre hazırlanır."
        },
        {
          id: 8,
          question: "Kamera ve veri izinlerine neden ihtiyaç var?",
          answer: "Kamera izni, çevrenizdeki spor ekipmanları olabilecek nesneleri taramak için gerekir; görüntüler işlenir ve kaydedilmez. Bildirim ve veri izinleri ise hatırlatma ve ilerleme takibi içindir."
        },
        {
          id: 9,
          question: "Egzersizler nasıl önerilmektedir?",
          answer: "Exereyes'ı kullanım sürecinde size özel egzersizler önerir. Bu öneriler, fiziksel kapasitenize, hedeflerinize ve egzersiz türüne göre hazırlanmıştır."
        },
        {
          id: 10,
          question: "Çevrimdışı çalışır mı?",
          answer: "Exereyes'ı indirip profilinizi oluşturduktan sonra temel egzersizleri çevrimdışı başlatabilirsiniz. İnternet geldiğinde ilerleme ve geri bildirimler otomatik senkronize edilir."
        },
        {
          id: 11,
          question: "Destek kanallarına nasıl ulaşırım?",
          answer: "Uygulama içindeki 'Destek' menüsünden support@exereyes.com adresine e-posta gönderebilirsiniz."
        }
      ]
    },
  {
    category: "Hesap Yönetimi",
    questions: [
      {
        id: 12,
        question: "Exereyes hesabımı ve verilerimi nasıl silebilirim?",
        answer: (
          <span>
            Kullanıcı hesabınızı ve verilerinizi dilediğiniz zaman silebilirsiniz. Bu işlem kalıcıdır ve geri alınamaz. Hesabınızı silmek için <Link to="/remove-user" className="text-primary-600 font-bold hover:underline hover:text-primary-700 transition-colors">Hesabımı Sil</Link> sayfasını ziyaret ederek yönergeleri takip edebilirsiniz.
          </span>
        )
      }
    ]
  }
];

const FAQ: React.FC = () => {
  const [activeId, setActiveId] = useState<number | null>(null);

  const toggleAccordion = (id: number) => {
    setActiveId(activeId === id ? null : id);
  };

  // Animasyon Varyantları
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <>
      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 bg-primary-900 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-20 pointer-events-none">
            <div className="absolute top-[-10%] left-[20%] w-[500px] h-[500px] bg-teal-500 rounded-full blur-[120px]"></div>
            <div className="absolute bottom-[-10%] right-[-10%] w-[400px] h-[400px] bg-primary-500 rounded-full blur-[100px]"></div>
        </div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
            <h1 className="text-4xl md:text-6xl font-display font-bold mb-6 text-white tracking-tight">
              Sık Sorulan Sorular
            </h1>
            <p className="text-lg md:text-xl mb-8 max-w-2xl mx-auto text-primary-100 leading-relaxed">
              Aklınıza takılanlar mı var? En çok merak edilen soruları sizin için derledik.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ACCORDION SECTION */}
      <Section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          
          {faqData.map((section, index) => (
            <div key={index} className="mb-12 last:mb-0">
              <motion.h3 
                className="text-xl font-bold text-primary-900 mb-6 flex items-center gap-2"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <span className="w-2 h-8 bg-primary-500 rounded-full inline-block"></span>
                {section.category}
              </motion.h3>

              <div className="space-y-4">
                {section.questions.map((item) => (
                  <motion.div 
                    key={item.id}
                    className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300"
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                  >
                    <button
                      onClick={() => toggleAccordion(item.id)}
                      className="w-full px-6 py-5 text-left flex items-center justify-between focus:outline-none group"
                    >
                      <span className={`font-semibold text-lg transition-colors duration-300 ${activeId === item.id ? 'text-primary-600' : 'text-gray-800'}`}>
                        {item.question}
                      </span>
                      <span className={`p-2 rounded-full transition-colors duration-300 ${activeId === item.id ? 'bg-primary-50 text-primary-600' : 'bg-gray-50 text-gray-400 group-hover:bg-gray-100'}`}>
                        {activeId === item.id ? <Minus size={20} /> : <Plus size={20} />}
                      </span>
                    </button>

                    <AnimatePresence>
                      {activeId === item.id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                        >
                          <div className="px-6 pb-6 text-gray-600 leading-relaxed border-t border-gray-50 pt-4">
                            {item.answer}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}

          {/* İletişim Çağrısı */}
          <motion.div 
            className="mt-16 bg-primary-900 rounded-3xl p-8 md:p-12 text-center relative overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
             <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full blur-3xl -mr-10 -mt-10"></div>
             
             <div className="relative z-10">
                <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-6 text-white backdrop-blur-sm">
                    <MessageCircle size={32} />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Başka bir sorunuz mu var?</h3>
                <p className="text-primary-100 mb-8 max-w-lg mx-auto">
                    Aradığınız cevabı bulamadıysanız bizimle iletişime geçmekten çekinmeyin. Ekibimiz size yardımcı olmaktan mutluluk duyacaktır.
                </p>
                <Link 
                    to="/contact" 
                    className="inline-flex items-center justify-center px-8 py-3 bg-white text-primary-900 rounded-xl font-bold hover:bg-primary-50 transition-colors shadow-lg"
                >
                    Bize Ulaşın
                </Link>
             </div>
          </motion.div>

        </div>
      </Section>
    </>
  );
};

export default FAQ;
